import React, { useState, useEffect, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useApi } from "./api";
import { useAuth } from "./auth";
const UserInfoContext = createContext({
  roles: [],
  isAccountFullySetup: false,
});
const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;

export const useUserInfo = () => useContext(UserInfoContext);

export const UserInfoContextProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const { getCurrentUser, getUser, getAccount } = useApi();
  const [userProfile, setUserProfile] = useState(() => {
    // Try to retrieve user info from localStorage on initial load
    try {
      const storedUserInfo = localStorage.getItem("userInfo");
      if (storedUserInfo) {
        return JSON.parse(storedUserInfo);
      }
    } catch (error) {
      console.error("Error parsing userInfo from localStorage:", error);
    }
    return {};
  });

  useEffect(() => {
    const getUserMetadata = async () => {
      // Check localStorage first
      console.log("getUserMetadata");
      try {
        const storedUserInfo = localStorage.getItem("userInfo");
        console.log("storedUserInfo", storedUserInfo);
        if (storedUserInfo) {
          const parsedUserInfo = JSON.parse(storedUserInfo);
          setUserProfile(parsedUserInfo);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error("Error parsing userInfo from localStorage:", error);
      }

      // If not in localStorage, fetch from API
      const userInfo = await getCurrentUser();

      if (userInfo.coachId) {
        const coachInfo = await getUser(userInfo.coachId);
        userInfo.coachAccountId = coachInfo.stripeAccountId;
      }
      setUserProfile(userInfo);
      // Save user info to localStorage
      try {
        localStorage.setItem("userInfo", JSON.stringify(userInfo));
      } catch (error) {
        console.error("Error saving userInfo to localStorage:", error);
      }
      setLoading(false);
    };
    if (user) {
      getUserMetadata();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Centralize Stripe account completeness check so it runs on every navigation/load
  useEffect(() => {
    const verifyStripeAccount = async () => {
      setLoading(true);
      const isPlatformOwner = userProfile?.id === PLATFORM_OWNER_USER_ID;
      // Platform owner is always "setup"
      if (isPlatformOwner) {
        setUserProfile((prev) => ({ ...prev, isAccountFullySetup: true }));
        setLoading(false);
        return;
      }
      if (!userProfile?.stripeAccountId) return;
      let isAccountFullySetup = true;
      try {
        const connectedAccount = await getAccount(userProfile.stripeAccountId);
        isAccountFullySetup =
          connectedAccount.charges_enabled && connectedAccount.payouts_enabled;
        if (
          !isAccountFullySetup &&
          window.location.pathname.indexOf("/coaching") === -1
        ) {
          window.location.href = "/coaching";
        }
      } catch (error) {
        console.error("Error checking connected Stripe account:", error);
      }
      // Keep a derived flag on the profile for easy UI checks
      setUserProfile((prev) => ({
        ...prev,
        isAccountFullySetup,
      }));
      setLoading(false);
    };
    if (userProfile?.isCoach) {
      verifyStripeAccount();
    }
  }, [
    userProfile?.stripeAccountId,
    getAccount,
    location?.pathname,
    userProfile?.isCoach,
  ]);
  return (
    <UserInfoContext.Provider value={{ ...userProfile, loading }}>
      {children}
    </UserInfoContext.Provider>
  );
};
